import { Fragment, useMemo, useState } from "react"
import type { JournalEntry, PermanentState } from "../types"
import type { KnowledgeCategory } from "../types"
import {
  abilities,
  getJournalEntryByKnowledgeId,
  getKnowledgeCategory,
} from "../game/content"
import { formatDay } from "../game/calendarEngine"
import { getEventDayForKnowledge } from "../game/scheduleEngine"
import { getAbilitySpriteStyle, getKnowledgeIconStyle } from "../game/sprites"

interface JournalScreenProps {
  permanent: PermanentState
  /** Today, so a dated entry can say whether it is still ahead of the player. */
  currentDay: number
  onBack: () => void
}

const SEEN_MARKER_PREFIX = "seen_"

// The label read out for each knowledge-category emblem. The icon is a visual
// accent on top of the entry's own title, so this only needs to name the kind.
const KNOWLEDGE_CATEGORY_LABEL: Record<KnowledgeCategory, string> = {
  omen: "Foreseen threat",
  rumor: "Rumour",
  dispatch: "Dispatch",
  route: "Route",
  victory: "Threat resolved",
  alliance: "Alliance",
  lore: "Lore",
  rival: "The rival",
  loop: "The loop",
  deadline: "The failing sun",
  doctrine: "Doctrine of war",
  triumph: "Triumph",
}

interface DatedEntry {
  entry: JournalEntry
  /** The day this entry is about, when it describes a scheduled event. */
  day: number | null
}

/**
 * A day number alone is only half a fact in a game whose whole loop is "get
 * there before it happens". The journal is read to plan, so every dated entry
 * says where it sits relative to today rather than leaving the player to
 * subtract the current day from it on every line.
 */
function relativeDay(day: number, currentDay: number): { text: string; className: string } {
  const delta = day - currentDay
  if (delta < 0) return { text: "already passed", className: "journal-when-past" }
  if (delta === 0) return { text: "today", className: "journal-when-now" }
  if (delta === 1) return { text: "tomorrow", className: "journal-when-soon" }
  return {
    text: `in ${delta} days`,
    className: delta <= 5 ? "journal-when-soon" : "journal-when-ahead",
  }
}

export function JournalScreen({ permanent, currentDay, onBack }: JournalScreenProps) {
  const [query, setQuery] = useState("")

  const learnedAbilities = abilities.filter(
    (ability) => (permanent.abilities[ability.id] ?? "none") !== "none",
  )

  const allEntries = useMemo<DatedEntry[]>(() => {
    // Skips the internal "seen_<dialogueId>" markers used only to let dialogue
    // be skipped on a repeat viewing.
    const entries = permanent.knowledge
      .filter((id) => !id.startsWith(SEEN_MARKER_PREFIX))
      .map((id) => getJournalEntryByKnowledgeId(id))
      .filter((entry): entry is JournalEntry => entry !== undefined)
      .map((entry) => ({ entry, day: getEventDayForKnowledge(entry.knowledgeId) }))

    // Anything tied to a day sorts by that day — the journal is a timetable
    // first and a diary second, and "most recently learned" was a useless
    // order for planning a loop around. Undated lore keeps up the rear in
    // discovery order.
    const dated = entries
      .filter((item): item is DatedEntry & { day: number } => item.day !== null)
      .sort((a, b) => a.day - b.day)
    const undated = entries.filter((item) => item.day === null)
    return [...dated, ...undated]
  }, [permanent.knowledge])

  const visibleEntries = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return allEntries
    return allEntries.filter(
      ({ entry, day }) =>
        entry.title.toLowerCase().includes(needle) ||
        entry.text.toLowerCase().includes(needle) ||
        (day !== null && `day ${day}`.includes(needle)),
    )
  }, [allEntries, query])

  return (
    <div className="screen journal-screen">
      <h1>Journal</h1>
      <p className="journal-intro">
        Everything I have learned, carried forward across every fall.
      </p>

      <h2>Skills</h2>
      {learnedAbilities.length === 0 ? (
        <p>Nothing learned yet.</p>
      ) : (
        <ul className="journal-ability-list">
          {learnedAbilities.map((ability) => {
            const spriteStyle = getAbilitySpriteStyle(ability.id)
            return (
              <li key={ability.id} className="journal-ability">
                {spriteStyle && (
                  <div
                    className="ability-icon"
                    style={spriteStyle}
                    role="img"
                    aria-label={ability.name}
                  />
                )}
                <div className="journal-ability-body">
                  <h3>
                    {ability.name}{" "}
                    <span className="journal-ability-level">
                      — {permanent.abilities[ability.id]}
                    </span>
                  </h3>
                  <p>{ability.description}</p>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <hr className="ornament-rule" />

      <h2>Knowledge</h2>
      {allEntries.length === 0 ? (
        <p>Nothing worth recording yet.</p>
      ) : (
        <>
          {/* Knowledge is permanent and only ever grows, so by the tenth loop
              this list is unnavigable without a filter. */}
          <label className="journal-search">
            <span>Search</span>
            <input
              type="search"
              value={query}
              placeholder="a place, a day, a name…"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>

          {visibleEntries.length === 0 ? (
            <p>Nothing in the journal matches that.</p>
          ) : (
            <ul className="journal-entry-list">
              {visibleEntries.map(({ entry, day }, index) => {
                // A small emblem for the kind of thing this is — the same
                // treatment the Skills list gives an ability. Entries whose
                // category has no cell yet simply show no icon.
                const category = getKnowledgeCategory(entry.knowledgeId)
                const iconStyle = getKnowledgeIconStyle(category)
                return (
                  <Fragment key={entry.knowledgeId}>
                    {/* A filigree break between entries; not before the first. */}
                    {index > 0 && (
                      <li
                        className="ornament-rule ornament-rule-tight"
                        aria-hidden="true"
                      />
                    )}
                  <li className="journal-entry">
                    {iconStyle && category && (
                      <div
                        className="knowledge-icon"
                        style={iconStyle}
                        role="img"
                        aria-label={KNOWLEDGE_CATEGORY_LABEL[category]}
                      />
                    )}
                    <div className="journal-entry-body">
                      <h3>
                        {day !== null && <span className="journal-day">{formatDay(day)}</span>}
                        {entry.title}
                        {day !== null &&
                          (() => {
                            const when = relativeDay(day, currentDay)
                            return (
                              <span className={`journal-when ${when.className}`}>
                                {when.text}
                              </span>
                            )
                          })()}
                      </h3>
                      <p>{entry.text}</p>
                    </div>
                  </li>
                  </Fragment>
                )
              })}
            </ul>
          )}
        </>
      )}

      <button type="button" onClick={onBack}>
        Back to Map
      </button>
    </div>
  )
}
